import { can } from '@af/shared';
import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { IconDots } from '@/components/ui/icons';
import { useRole } from '@/lib/permissions';
import { useKeyboardInset } from '@/lib/use-media';
import { cn } from '@/lib/utils';
import { NAV } from './nav';
import { NAV_ICONS } from './Sidebar';

/** Phone tab bar: the daily destinations within thumb reach; everything else lives behind "More" (the drawer). */
const TABS = ['/', '/chat', '/tasks', '/approvals'] as const;

const tabCls =
  'relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-2xs font-medium transition-colors';

export function BottomNav({ pendingApprovals, onMore }: { pendingApprovals?: number; onMore: () => void }) {
  const { t } = useTranslation();
  const role = useRole();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const keyboard = useKeyboardInset();
  // A conversation owns the whole screen (composer at the bottom); the keyboard hides the bar too.
  if (keyboard > 100 || pathname.startsWith('/chat/') || pathname === '/master') return null;
  const items = TABS.map((to) => NAV.find((n) => n.to === to)).filter(
    (n): n is (typeof NAV)[number] => !!n && (!n.perm || can(role, n.perm)),
  );
  const active = (to: string) =>
    to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
  return (
    <nav
      aria-label={t('shell.bottomNav')}
      className="flex shrink-0 border-t border-border bg-bg lg:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {items.map((n) => {
        const Icon = NAV_ICONS[n.to];
        const count = n.badge === 'approvals' ? pendingApprovals : 0;
        const on = active(n.to);
        return (
          <Link
            key={n.to}
            to={n.to}
            aria-current={on ? 'page' : undefined}
            className={cn(tabCls, on ? 'text-fg' : 'text-muted')}
          >
            <span className="relative">
              <Icon size={22} />
              {count ? (
                <span className="absolute -top-1 -right-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-warn px-1 font-mono text-[10px] font-semibold text-bg">
                  {count > 9 ? '9+' : count}
                </span>
              ) : null}
            </span>
            <span className="max-w-full truncate">{t(n.key)}</span>
          </Link>
        );
      })}
      <button type="button" onClick={onMore} className={cn(tabCls, 'text-muted')}>
        <IconDots size={22} />
        <span>{t('shell.more')}</span>
      </button>
    </nav>
  );
}
